import React, { useMemo } from 'react';
import {
  createDefaultFieldFactory,
  FieldTypes,
  SingleLineText,
  MultipleLineText,
  Email,
  Telephone,
  NumberField,
  DateField,
  Password,
  Checkbox,
  CheckboxList,
  DropdownList,
  ListBox,
  RadioButtonList,
  FileUpload,
} from '@sitecore-jss/sitecore-jss-react-forms';

/*
  Mouseflow's Form Analytics identifies a field exclusively by its HTML `name`
  attribute, and Sitecore FXB renders that as a GUID path
  (`fxb.<form guid>.Fields[<field guid>].Value`), so its dashboard groups fields
  by GUID instead of by "First name" / "Email address".

  This factory wraps the stock JSS field templates so the rendered `name` is the
  human-readable Sitecore model name (`first-name`, `email-address`, ...) from the
  moment the element enters the DOM. The Sitecore contract is untouched: the POST
  body is built by `serializeForm` from the layout-service JSON, never from the
  DOM, and the HOC maps `onChange` back to the GUID so React form state keeps
  using the same keys `getCurrentFieldState` reads.
*/

// Fields that must keep their GUID name.
const EXCLUDED_MODEL_NAMES = [
  // Honeypots. `username` / `company` are the names browsers most want to
  // autofill, and a filled honeypot silently drops a real lead. They are
  // invisible fields, so Mouseflow does not need them named.
  'username',
  'company',
  // Generic ("Jane") forms append their own hidden inputs named `dd1`/`dd2`/`dd3`
  // in `getMappings` (dynaMXFormCaptureSubmit.js). Renaming these selects to the
  // same value would give Dynamics two elements per name to choose from.
  'dd1',
  'dd2',
  'dd3',
];

export const getFriendlyFieldName = (field) => {
  const modelName = field?.model?.name;
  if (!modelName || EXCLUDED_MODEL_NAMES.includes(modelName)) {
    return null;
  }

  return modelName;
};

const withFriendlyName = (DefaultComponent) => {
  const FriendlyNameField = (props) => {
    const realName = props.field?.valueField?.name;
    const friendly = getFriendlyFieldName(props.field);

    // Stable identity: FormTracker mutates the field object it is handed
    // (focusedAtTick / originalValue), so it must not be a new object each render.
    const patchedField = useMemo(
      () =>
        friendly && realName
          ? {
              ...props.field,
              valueField: { ...props.field.valueField, name: friendly },
            }
          : props.field,
      [props.field, friendly, realName],
    );

    if (patchedField === props.field) {
      return <DefaultComponent {...props} />;
    }

    // The templates call back with `field.valueField.name`, which is now the
    // friendly name. Remap it to the GUID so form state, getCurrentFieldState
    // and serializeForm are all completely unaffected.
    const onChange = (fieldName, value, isValid, errors) =>
      props.onChange(realName, value, isValid, errors);

    return (
      <DefaultComponent {...props} field={patchedField} onChange={onChange} />
    );
  };

  FriendlyNameField.displayName = `withFriendlyName(${
    DefaultComponent.displayName || DefaultComponent.name || 'Field'
  })`;

  return FriendlyNameField;
};

// Start from the defaults so Section / TextField / Button stay untouched, then
// override only the value-bearing types. Button is deliberately not wrapped: it
// renders `field.buttonField.name`, which `serializeForm` matches on to identify
// the submit button.
const friendlyNameFieldFactory = createDefaultFieldFactory();

[
  [FieldTypes.SingleLineText, SingleLineText],
  [FieldTypes.MultipleLineText, MultipleLineText],
  [FieldTypes.Email, Email],
  [FieldTypes.Telephone, Telephone],
  [FieldTypes.NumberField, NumberField],
  [FieldTypes.DateField, DateField],
  [FieldTypes.Password, Password],
  [FieldTypes.Checkbox, Checkbox],
  [FieldTypes.CheckboxList, CheckboxList],
  [FieldTypes.DropdownList, DropdownList],
  [FieldTypes.ListBox, ListBox],
  [FieldTypes.RadioButtonList, RadioButtonList],
  [FieldTypes.FileUpload, FileUpload],
].forEach(([type, Component]) =>
  friendlyNameFieldFactory.setComponent(type, withFriendlyName(Component)),
);

export default friendlyNameFieldFactory;
